/** Media buffering checkpoints used by the live-workspace startup screen. */

export const LIVE_BUFFER_SECONDS = 30
export const FILE_STARTUP_BUFFER_SECONDS = 5

export interface MediaLoadProgress {
  value: number | null
  bufferedSeconds: number | null
  targetSeconds: number | null
  /** True once the browser reports enough data to begin playback. */
  playable: boolean
}

export interface MediaProgressElement {
  buffered: TimeRanges
  currentTime: number
  duration: number
  readyState: number
}

/**
 * Return the contiguous buffered runway from the current playback position.
 * Disconnected future ranges do not count toward startup readiness.
 */
function bufferedRunway(element: MediaProgressElement) {
  if (!element.buffered.length) return null
  const position = Number.isFinite(element.currentTime) ? Math.max(0, element.currentTime) : 0
  let runway = 0
  let found = false
  const epsilon = 0.05
  for (let index = 0; index < element.buffered.length; index += 1) {
    const start = element.buffered.start(index)
    const end = element.buffered.end(index)
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) continue
    if (!found) {
      if (end <= position) continue
      if (start > position + epsilon) return 0
      runway = Math.max(0, end - position)
      found = true
      continue
    }
    if (start > position + runway + epsilon) break
    runway = Math.max(runway, end - position)
  }
  return found && runway > 0 ? runway : null
}

/**
 * Measure actual media buffering against the amount needed to begin playback.
 * A null value is intentional when the browser cannot expose a finite total or
 * has not buffered a contiguous range from the current position yet.
 */
export function measureMediaLoadProgress(element: MediaProgressElement, live: boolean, liveBufferSeconds = LIVE_BUFFER_SECONDS): MediaLoadProgress {
  const buffered = bufferedRunway(element)
  const playable = element.readyState >= 3 // HAVE_FUTURE_DATA
  if (buffered == null) return { value: null, bufferedSeconds: null, targetSeconds: null, playable }
  if (live) {
    return {
      value: Math.max(0, Math.min(1, buffered / liveBufferSeconds)),
      bufferedSeconds: buffered,
      targetSeconds: liveBufferSeconds,
      playable,
    }
  }
  const duration = Number.isFinite(element.duration) && element.duration > 0 ? element.duration : null
  const target = duration == null ? null : Math.min(FILE_STARTUP_BUFFER_SECONDS, duration)
  if (target == null) return { value: null, bufferedSeconds: buffered, targetSeconds: null, playable }
  return {
    value: Math.max(0, Math.min(1, buffered / target)),
    bufferedSeconds: buffered,
    targetSeconds: target,
    playable,
  }
}

/** Local monitoring can start before cloud transcription; both paths require playable media. */
export function isLiveWorkspaceReady(playerState: 'waiting' | 'ready' | 'empty' | 'error', playable: boolean, transcriptReady: boolean, localPreview = false) {
  return playerState === 'ready' && playable && (transcriptReady || localPreview)
}
